/**
 * What counts as a document this product can send for signature.
 *
 * One list, in `packages/core` rather than in a screen, because more than one surface asks and the
 * answers must agree: the composer's dropzone, the documents step's file picker and drop handler,
 * and the Envelopes card that now starts an envelope from a file. A picker that offers a format the
 * drop handler refuses is the defect this prevents, and it is the same reason `photo.ts` next door
 * owns what a profile picture may be.
 *
 * **This is a courtesy, not a control.** The API sniffs the magic bytes of what arrives
 * (`uploads/magic-bytes`), because an extension and a browser-declared `type` are both claims by
 * the uploader. What this does is decline early, so somebody who picks a `.heic` off their phone is
 * told in the same instant rather than after an upload that ends in a status code.
 *
 * ## The defect this list carried until 2026-09-12
 *
 * It named six formats — `.pdf,.docx,.doc,.odt,.xlsx,.pptx` — while `magic-bytes.ts`,
 * `decideFormat`, `CONVERTIBLE_FORMATS` in the doc-convert container and `document-engine.md` §2
 * all named sixteen. So every picker in the console greyed out `.xls`, `.ods`, `.odp`, `.ppt`,
 * `.rtf`, `.txt`, `.html`, `.png`, `.jpg` and `.webp`, and every drop handler refused them with
 * "A XLS file cannot be signed" — about files the API accepts and LibreOffice converts.
 *
 * That is this module's own purpose pointing the wrong way. Declining early is worth doing because
 * it saves a sender a round trip to a refusal; a decline the server would NOT have made is not a
 * saved round trip, it is a wrong answer given faster. The founder hit it on an Excel file.
 *
 * ## Why it is still a literal, and what stops it drifting again
 *
 * Deriving it from `magic-bytes.ts` is the obvious fix and it is the wrong one HERE: five route
 * chunks import this module, so rolldown hoists it into the shared chunk every route loads, and
 * that chunk is what the editor's 150 KB budget is measured against — a budget it currently sits
 * at exactly (`infra/verify-bundle-budget.mjs`, 0% headroom). Importing the sniffer to read two
 * tables put the editor over it, for tables the browser has no other use for.
 *
 * So the list stays a literal and `packages/core/test/documents-files.test.ts` holds it to
 * `magic-bytes.ts`'s own tables by SET EQUALITY, inside `pnpm verify`. A format added to the
 * sniffer and not to this line is a red gate, not a silent divergence — which is the guarantee the
 * import would have bought, at no weight.
 */

/**
 * The `accept` attribute of every file input that takes a document.
 *
 * Every extension `formatFromFilename` answers for, in the order `magic-bytes.ts` declares the
 * formats, plus `application/pdf` for the pickers that match on media type. The test above fails
 * if this and the sniffer's `EXTENSIONS` table ever name different sets.
 */
export const ACCEPT_DOCUMENTS =
  '.pdf,.docx,.xlsx,.pptx,.odt,.ods,.odp,.doc,.xls,.ppt,.rtf,.txt,.html,.htm,.png,.jpg,.jpeg,.webp,application/pdf'

/**
 * The same set in the words a sender reads, so the surfaces that name it agree.
 *
 * Six screens spelled this out for themselves — "PDF or DOCX", "PDF or Office files", "PDF, DOCX,
 * DOC, ODT, XLSX or PPTX", "PDF, Word, OpenDocument, Excel and PowerPoint" — and all six were
 * wrong in the same direction. Families rather than suffixes: "Word" is what a sender calls `.doc`
 * and `.docx`, and a sentence listing eighteen extensions is one nobody finishes reading.
 */
export const ACCEPTED_DOCUMENTS_SENTENCE =
  'PDF, Word, Excel, PowerPoint, OpenDocument, RTF, text, HTML, or a PNG, JPEG or WebP image'

/**
 * The extensions `ACCEPT_DOCUMENTS` names, derived rather than restated: two lists is how a picker
 * and a dropzone come to disagree about what a screen takes.
 */
const ACCEPTED_EXTENSIONS = ACCEPT_DOCUMENTS.split(',')
  .map((part) => part.trim())
  .filter((part) => part.startsWith('.'))

/**
 * Whether a filename names a format this product takes.
 *
 * The extension is read from the LAST dot and nothing else is inferred from the name, which is the
 * rule `formatFromFilename` follows on the server: `invoice.pdf.exe` is refused rather than read as
 * the PDF it is pretending to be. Matching with `endsWith` would have accepted it.
 */
export function isAcceptedDocumentName(name: string): boolean {
  const dot = name.lastIndexOf('.')
  if (dot < 0 || dot === name.length - 1) return false
  return ACCEPTED_EXTENSIONS.includes(name.slice(dot).toLowerCase())
}
